import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type {
  DihedralProfile,
  FailedTorsionRow,
  FittedTorsionRow,
  FragmentStatus,
  SkippedDihedralRow,
} from "@/api/affdo";
import { apiFetch } from "@/api/client";
import { retryQuery } from "@/api/retry";

// Wire-format mirrors src/flowstack/server/routes/analysis_report.py (U7.3,
// D71-iii). The per-fragment/per-torsion field shapes are IDENTICAL to
// api/affdo.ts's live-projection types on purpose — this is the same data,
// materialised once instead of recomputed per view — so FragmentSummaryTable/
// DihedralRMSDGrid/SkippedDihedralsList accept either without modification.

export interface FragmentAnalysis {
  torsion_refinement_result_id: string;
  fragment_id: string;
  torsion_set_id: string;
  n_fitted: number;
  n_failed: number;
  n_total: number;
  max_fit_rmsd_kcal_per_mol: number | null;
  mean_fit_rmsd_kcal_per_mol: number | null;
  status: FragmentStatus;
  fitted: FittedTorsionRow[];
  failed: FailedTorsionRow[];
  profiles: DihedralProfile[];
}

export interface AnalysisReport {
  run_id: string;
  fragments: FragmentAnalysis[];
  skipped: SkippedDihedralRow[];
  input_artifact_hashes: string[];
}

export interface AnalysisReportEnvelope {
  run_id: string;
  available: boolean;
  reason: string | null;
  /** When THIS snapshot was materialised — not when the underlying science
   * ran. `null` when `available` is false. */
  generated_at: string | null;
  report: AnalysisReport | null;
}

export function analysisReportQueryKey(runId: string): readonly unknown[] {
  return ["runs", runId, "analysis-report"] as const;
}

async function fetchAnalysisReport(runId: string): Promise<AnalysisReportEnvelope> {
  return apiFetch<AnalysisReportEnvelope>(`/api/runs/${encodeURIComponent(runId)}/analysis-report`);
}

/** The latest materialised report for a run. Never triggers generation
 * itself — pair with `useMaterializeAnalysisReport` for that. */
export function useAnalysisReport(runId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: analysisReportQueryKey(runId),
    queryFn: async (): Promise<AnalysisReportEnvelope> => fetchAnalysisReport(runId),
    enabled,
  });
}

/**
 * The same read for SEVERAL runs at once — the cross-run overlay's source
 * (VIZ U2.1).
 *
 * `useQueries` rather than a new endpoint: the selection is a handful of runs
 * chosen in the browser, each report is already a materialised snapshot with
 * its own cache entry, and the key and fetcher are shared verbatim with
 * `useAnalysisReport` above. So a run whose report the user already opened is
 * served from cache, and `useMaterializeAnalysisReport`'s cache seeding is
 * picked up here for free. A `/analysis-reports?runs=` batch route would buy
 * one round trip and cost a second server-side projection of the same data —
 * the plan's "webapp-only unless the overlay genuinely needs an endpoint it
 * does not have" test, answered.
 *
 * Never triggers generation, exactly like the single-run hook: a run with no
 * report reads as "not generated", which is a different claim from "no data"
 * and is the caller's to render.
 *
 * **The retry policy is load-bearing here** (Rupert PR-993 r0), so this
 * fan-out states it rather than inheriting it: {@link retryQuery} — the same
 * function `main.tsx` installs as the app-wide default, not a second copy —
 * returns `false` for any 4xx. Were it TanStack's own default of three
 * exponential-backoff retries, the canonical broken-link case this surface is
 * built around — a stale or foreign run id — would cost four requests and
 * delay its "could not be found" line by seconds, during which the overlay
 * renders short with nothing explaining it. Naming it here means a future
 * change to the app default cannot quietly take that guarantee away
 * (flowstack#994 item 3).
 */
export function useAnalysisReports(
  runIds: readonly string[]
): UseQueryResult<AnalysisReportEnvelope>[] {
  return useQueries({
    queries: runIds.map((runId) => ({
      queryKey: analysisReportQueryKey(runId),
      queryFn: async (): Promise<AnalysisReportEnvelope> => fetchAnalysisReport(runId),
      enabled: Boolean(runId),
      retry: retryQuery,
    })),
  });
}

/** Generate (or regenerate) the report. On success, seeds the GET query's
 * cache directly with the response rather than invalidating + refetching —
 * one round trip instead of two, and the materialize call already returned
 * the freshest data there is. */
export function useMaterializeAnalysisReport(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AnalysisReportEnvelope> =>
      apiFetch<AnalysisReportEnvelope>(`/api/runs/${encodeURIComponent(runId)}/analysis-report`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(analysisReportQueryKey(runId), data);
    },
  });
}
